import { __read } from "tslib";
import { isFocusableHtmlElement } from '@office-start/dom-utility';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFocusToastContainer } from './hooks';
import { useToastContainerStyles } from './ToastContainerControl.styles';
var toastTimeoutMilliseconds = 7000;
export var ToastContainerControl = function (_a) {
    var ariaBusy = _a.ariaBusy, ariaLabel = _a.ariaLabel, ariaLive = _a.ariaLive, autoDismiss = _a.autoDismiss, callToAction = _a.callToAction, children = _a.children, onDismiss = _a.onDismiss;
    var toastElementControlRef = useRef(null);
    var styles = useToastContainerStyles();
    var _b = __read(useState(children ? true : false), 2), canShouldRender = _b[0], setCanShouldRender = _b[1];
    // Get a reference to last active element
    var initialActiveElementRef = useRef(isFocusableHtmlElement(document.activeElement)
        ? document.activeElement
        : null);
    // Styles for animating the toast container sliding in and out
    var _c = __read(useState("".concat(styles.toastContainer, " ").concat(styles.toastContainerSlideUpIn)), 2), inOutStyles = _c[0], setInOutStyles = _c[1];
    // Styles for wrapping the container so we can hide the overflow
    var _d = __read(useState("".concat(styles.toastWrapper, " ").concat(styles.toastWrapperAnimating)), 2), toastWrapperStyles = _d[0], setToastWrapperStyles = _d[1];
    // When timeout or user clicks trigger the animation
    var onShouldDismiss = useCallback(function () {
        if (canShouldRender) {
            setCanShouldRender(false);
            setInOutStyles("".concat(styles.toastContainer, " ").concat(styles.toastContainerSlideDownOut));
        }
    }, [
        canShouldRender,
        setCanShouldRender,
        setInOutStyles,
        styles.toastContainer,
        styles.toastContainerSlideDownOut,
    ]);
    var handleOnAnimationStart = useCallback(function () {
        setToastWrapperStyles("".concat(styles.toastWrapper, " ").concat(styles.toastWrapperAnimating));
    }, [
        setToastWrapperStyles,
        styles.toastWrapper,
        styles.toastWrapperAnimating,
    ]);
    // When animation completes and 'canShouldRender' is false (ie clicked or timed out), call onDismiss
    var handleOnAnimationEnd = useCallback(function () {
        if (!canShouldRender) {
            onDismiss();
            // Put focus back on element that triggered the toast
            if (initialActiveElementRef.current &&
                initialActiveElementRef.current !== document.activeElement) {
                initialActiveElementRef.current.focus();
            }
        }
        setToastWrapperStyles("".concat(styles.toastWrapper, " ").concat(styles.toastWrapperStill));
    }, [
        onDismiss,
        canShouldRender,
        setToastWrapperStyles,
        styles.toastWrapper,
        styles.toastWrapperStill,
    ]);
    useEffect(function () {
        var timeout;
        if (children && autoDismiss) {
            timeout = setTimeout(function () {
                onShouldDismiss();
            }, toastTimeoutMilliseconds);
        }
        return function () {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, [autoDismiss, children, onShouldDismiss]);
    useFocusToastContainer(toastElementControlRef, callToAction);
    if (!children) {
        return null;
    }
    return (React.createElement("div", { "aria-busy": ariaBusy, "aria-label": ariaLabel, "aria-live": ariaLive, "aria-keyshortcuts": "Control+F6", className: toastWrapperStyles, onAnimationStart: handleOnAnimationStart, onAnimationEnd: handleOnAnimationEnd, ref: function (ref) { return (toastElementControlRef.current = ref); }, role: "region", 
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex: 0 },
        React.createElement("div", { className: inOutStyles }, children)));
};
//# sourceMappingURL=ToastContainerControl.js.map